import { useSyncExternalStore } from 'react'
import i18n, { DEFAULT_LANGUAGE, isLanguage, type Language } from '@/i18n'

export type Theme = 'light' | 'dark' | 'system'
export const THEMES: Theme[] = ['light', 'dark', 'system']

export interface Preferences {
  locale: Language
  theme: Theme
}

/** Same keys as the inline boot script in index.html (applies them before first paint). */
const LOCALE_KEY = 'schooleduca.locale'
const THEME_KEY = 'schooleduca.theme'

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Private mode / blocked storage: preferences still apply for this session.
  }
}

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark' || value === 'system'
}

const storedLocale = read(LOCALE_KEY)
const storedTheme = read(THEME_KEY)
let state: Preferences = {
  locale: isLanguage(storedLocale) ? storedLocale : DEFAULT_LANGUAGE,
  theme: isTheme(storedTheme) ? storedTheme : 'light',
}
const listeners = new Set<() => void>()
const media =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null

export function resolvedTheme(theme: Theme = state.theme): 'light' | 'dark' {
  if (theme === 'system') return media?.matches ? 'dark' : 'light'
  return theme
}

function applyToDocument(): void {
  const root = document.documentElement
  root.dataset.theme = resolvedTheme()
  root.lang = state.locale
  if (i18n.language !== state.locale) void i18n.changeLanguage(state.locale)
}

// "System" follows the OS setting live.
media?.addEventListener('change', () => {
  if (state.theme === 'system') {
    applyToDocument()
    for (const l of listeners) l()
  }
})

applyToDocument()

/** Optional server sync, registered by the auth provider while a user is signed in. */
let persistRemote: ((prefs: Preferences) => void) | null = null

export function setRemotePersistence(fn: ((prefs: Preferences) => void) | null): void {
  persistRemote = fn
}

function update(patch: Partial<Preferences>, { remote }: { remote: boolean }): void {
  const next = { ...state, ...patch }
  if (next.locale === state.locale && next.theme === state.theme) return
  state = next
  write(LOCALE_KEY, state.locale)
  write(THEME_KEY, state.theme)
  applyToDocument()
  for (const l of listeners) l()
  if (remote) persistRemote?.(state)
}

export const preferences = {
  get: (): Preferences => state,
  setLocale: (locale: Language) => update({ locale }, { remote: true }),
  setTheme: (theme: Theme) => update({ theme }, { remote: true }),
  /** Adopt the signed-in user's saved preferences (they win over this browser's last choice). */
  adopt: (prefs: { locale: string; theme: string }) =>
    update(
      {
        locale: isLanguage(prefs.locale) ? prefs.locale : state.locale,
        theme: isTheme(prefs.theme) ? prefs.theme : state.theme,
      },
      { remote: false },
    ),
}

export function usePreferences(): Preferences & { resolvedTheme: 'light' | 'dark' } & typeof preferences {
  const snapshot = useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => state,
  )
  return { ...snapshot, resolvedTheme: resolvedTheme(snapshot.theme), ...preferences }
}
